import clsx from 'clsx';
import _range from 'lodash/range';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import ChevronDown from '@/assets/icons/chevron-down.svg';
import { CloseIcon } from '@/components/icons/close';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Typography } from '@/components/ui/typography';

import { Checkbox } from './checkbox';
import { Skeleton } from './skeleton';

type Option = {
  label: React.ReactNode;
  value: string | boolean | number | undefined;
};

type SelectProps<T> = {
  id: string;
  options: Option[];
  selectedValues: T[];
  onApply?: (selected: T[], key: string) => void;
  onChange?: (selected: T[], key: string) => void;
  isMultiSelect?: boolean;
  prefixText?: string;
  placeholder?: string;
  isLoading?: boolean;
  query?: string;
  setQuery?: (q?: string) => void;
  className?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  error?: string;
};

function Select<T>({
  id,
  options = [],
  selectedValues,
  onApply,
  onChange,
  isMultiSelect = true,
  prefixText,
  placeholder,
  isLoading,
  query = '',
  setQuery,
  className,
  isClearable = false,
  isDisabled = false,
  onOpenChange,
  error,
}: SelectProps<T>) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [tempSelectedValues, setTempSelectedValues] = useState(selectedValues);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setTempSelectedValues(selectedValues);
  }, [selectedValues]);

  const toggleSelection = useCallback(
    (value: T) => {
      if (isMultiSelect) {
        setTempSelectedValues(prev => {
          const newValues = prev.includes(value)
            ? prev.filter(item => item !== value)
            : [...prev, value];
          if (onChange) {
            onChange(newValues, id);
          }
          return newValues;
        });
      } else {
        setTempSelectedValues([value]);
        (onChange ?? onApply)?.([value], id);
        setOpen(false);
      }
    },
    [isMultiSelect, id, onApply, onChange, setOpen],
  );

  const mainText = useMemo(() => {
    if (isMultiSelect) {
      if (tempSelectedValues.length > 0)
        return intl.formatMessage(
          {
            defaultMessage: '{count} selected',
            description: 'Multi-select dropdown: number of selected items',
          },
          {
            count: tempSelectedValues.length,
          },
        );
      return prefixText ? 'All' : null;
    }

    const selectedOption = options.find(
      opt => opt.value === tempSelectedValues[0],
    );
    return selectedOption?.label ?? null;
  }, [tempSelectedValues, isMultiSelect, options, prefixText, intl]);

  const handleApply = () => {
    onApply?.(tempSelectedValues, id);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setTempSelectedValues([]);
    (onChange ?? onApply)?.([], id);
    setOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !onChange) setTempSelectedValues(selectedValues);
    setQuery?.();
    setOpen(open);
    onOpenChange?.(open);
  };

  // Calculate the width of the popover based on the width of the trigger. Minimum width is 240px.
  const popoverWidth = useMemo(() => {
    if (open && triggerRef.current) {
      return Math.max(240, triggerRef.current.offsetWidth);
    }
    return undefined;
  }, [open]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="secondary"
          className={clsx('rounded-sm', className, error && 'border-fx-rose')}
          disabled={isDisabled}
        >
          <ButtonText
            prefixText={prefixText}
            mainText={mainText}
            placeholder={placeholder}
            isLoading={isLoading}
          />
          {isClearable && tempSelectedValues.length > 0 ? (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              className="ml-auto h-5 w-5 cursor-pointer"
            >
              <CloseIcon className="text-fx-neutral-400 hover:text-fx-primary mt-0.5" />
            </span>
          ) : (
            <img src={ChevronDown} alt="Chevron down" className="ml-auto" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="pointer-events-auto max-h-72 min-w-[200px] overflow-hidden !p-0"
        align="start"
        style={popoverWidth ? { width: `${popoverWidth}px` } : undefined}
        onOpenAutoFocus={setQuery ? undefined : e => e.preventDefault()}
        onWheel={e => e.stopPropagation()}
      >
        <Command shouldFilter={false} className="h-auto max-h-full">
          {setQuery && (
            <CommandInput
              placeholder={intl.formatMessage({
                defaultMessage: 'Search...',
                description:
                  'Filter search within select / command dropdown (same as combobox)',
              })}
              className="h-9"
              value={query}
              onValueChange={setQuery}
            />
          )}
          <CommandList className="max-h-60 overscroll-contain">
            <Content isLoading={isLoading} options={options}>
              <CommandGroup className="overflow-visible">
                {options.map(option => {
                  const isSelected = tempSelectedValues.some(
                    value => value === option.value,
                  );
                  return (
                    <CommandItem
                      key={String(option.value)}
                      onSelect={() => toggleSelection(option.value as T)}
                      className={clsx(isSelected && 'bg-fx-neutral-100')}
                    >
                      {isMultiSelect ? (
                        <Checkbox
                          checked={isSelected}
                          label={option.label}
                          className="pointer-events-none"
                        />
                      ) : (
                        option.label
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </Content>
          </CommandList>
        </Command>
        {isMultiSelect && onApply && (
          <div className="flex justify-between border-t border-gray-200 p-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="text-fx-primary"
            >
              <FormattedMessage
                defaultMessage="Clear"
                description="Multi-select dropdown: reset temporary selections"
              />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleApply}
              className="text-fx-primary"
            >
              <FormattedMessage
                defaultMessage="Apply"
                description="Multi-select dropdown: confirm and close with current choices"
              />
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface ButtonTextProps {
  prefixText?: string;
  mainText: React.ReactNode | null;
  placeholder?: string;
  isLoading?: boolean;
}

function ButtonText({
  prefixText,
  mainText,
  placeholder,
  isLoading,
}: ButtonTextProps) {
  if (isLoading) return <Skeleton className="h-4 w-2/3" />;

  if (prefixText || mainText)
    return (
      <div className="flex min-w-0 items-center gap-2">
        {prefixText && <Typography variant="body1">{prefixText}</Typography>}
        {mainText && (
          <Typography variant="body2" className="truncate">
            {mainText}
          </Typography>
        )}
      </div>
    );

  if (placeholder)
    return <Typography variant="inactive1">{placeholder}</Typography>;

  return null;
}

interface ContentProps {
  isLoading?: boolean;
  options: Option[];
  children: React.ReactNode;
}

function Content({ isLoading, options, children }: ContentProps) {
  if (isLoading)
    return (
      <div className="space-y-4 p-4">
        {_range(5).map(i => (
          <Skeleton key={i} className="h-5 w-full" />
        ))}
      </div>
    );

  if (!isLoading && options.length === 0)
    return (
      <CommandEmpty>
        <Typography variant="body1">
          <FormattedMessage
            defaultMessage="No options found!"
            description="Empty select: search or data returned no matches"
          />
        </Typography>
      </CommandEmpty>
    );

  return <>{children}</>;
}

type FormSelectProps<T, TFieldValues extends FieldValues = FieldValues> = {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  error?: string;
  label?: string;
  description?: string;
  required?: boolean;
  /** When provided, called before applying the change. Return false (or resolve to false) to prevent the change. */
  onBeforeChange?: (selected: T[]) => void | boolean | Promise<void | boolean>;
  /**
   * Called after the form field value has been committed. Use this for side
   * effects (e.g. recomputing dependent fields). Mirrors the `register({ onChange })`
   * contract — runs after commit, so it's safe to call `setValue` on sibling fields.
   */
  onChange?: (selected: T[]) => void;
  className?: string;
} & Omit<SelectProps<T>, 'id' | 'selectedValues' | 'onApply' | 'onChange'>;

/**
 * A wrapper around the Select component that integrates with react-hook-form.
 * Uses onChange (immediate selection) instead of onApply (deferred with Apply button).
 */
function FormSelect<T, TFieldValues extends FieldValues>({
  name,
  control,
  error,
  label,
  description,
  required,
  isMultiSelect,
  onBeforeChange,
  onChange,
  className,
  ...props
}: FormSelectProps<T, TFieldValues>) {
  const id = useId();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div className={clsx('flex w-full flex-col gap-1', className)}>
          {/* Label */}
          {label && (
            <label htmlFor={id}>
              <Typography variant="subtitle2">
                {label} {required && <span className="text-fx-rose">*</span>}
              </Typography>
            </label>
          )}

          {/* Small description below the label */}
          {description && (
            <Typography variant="small1" className="!text-fx-neutral-600">
              {description}
            </Typography>
          )}

          {/* Select component */}
          <Select
            id={name}
            isMultiSelect={isMultiSelect}
            selectedValues={
              isMultiSelect
                ? (field.value ?? [])
                : field.value
                  ? [field.value]
                  : []
            }
            onChange={selected => {
              void Promise.resolve(onBeforeChange?.(selected) ?? true).then(
                allowChange => {
                  if (allowChange === false) return;
                  field.onChange(
                    isMultiSelect ? selected : (selected[0] ?? null),
                  );
                  onChange?.(selected);
                },
              );
            }}
            error={error}
            {...props}
          />
          {error && (
            <Typography variant="small1" className="!text-fx-rose">
              {error}
            </Typography>
          )}
        </div>
      )}
    />
  );
}

export { type Option, type SelectProps, FormSelect, Select };
